import React, { FC } from 'react'
import { Box, Paper, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { DataLoad, TableToolbar } from 'src/components/ui'
import { useSensorsBillingEstimatesRequest } from 'src/hooks/api'
import { BillingEstimatesGridRow, buildBillingEstimatesGridRows, formatUsd } from './billingEstimatesRows'

const formatAmount = ({ value }: { value?: number | null }) => (value === null || value === undefined ? '-' : value)

const columns: GridColDef<BillingEstimatesGridRow>[] = [
  {
    field: 'title',
    headerName: 'Devices',
    flex: 1.5,
    minWidth: 260,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
        <Box sx={{ whiteSpace: 'normal' }}>{row.title}</Box>
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{row.hint}</Box>}>
          <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.6, cursor: 'help', flexShrink: 0 }} />
        </Tooltip>
      </Box>
    )
  },
  {
    field: 'amount',
    headerName: 'Amount',
    flex: 1,
    minWidth: 120,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: formatAmount
  },
  {
    field: 'ratePerDevice',
    headerName: 'Selected tier',
    flex: 1,
    minWidth: 120,
    sortable: false,
    disableColumnMenu: true,
    // Monthly per-device tier price ($4.99); the Total row and empty buckets have no tier
    valueFormatter: ({ value }) => formatUsd(value as number | null | undefined)
  },
  {
    field: 'estimatedCost',
    headerName: 'Estimated Cost',
    flex: 1,
    minWidth: 120,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: ({ value }) => formatUsd(value as number | null | undefined)
  }
]

export const SensorBillingEstimates: FC = () => {
  const { data, error, isInitialLoading, isError } = useSensorsBillingEstimatesRequest()
  const rows = buildBillingEstimatesGridRows(data)

  return (
    <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
      <Paper>
        <DataGridPremium<BillingEstimatesGridRow>
          autoHeight
          pagination={false}
          hideFooterPagination
          disableRowSelectionOnClick
          rows={rows}
          columns={columns}
          rowCount={rows.length}
          getRowId={(row) => row.key}
          getRowHeight={() => 'auto'}
          rowThreshold={2}
          columnThreshold={2}
          slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_billing_estimates`} /> }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { py: 1 },
            '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: 'rgba(0, 0, 0, 0.6)',
              overflow: 'visible'
            }
          }}
        />
      </Paper>
    </DataLoad>
  )
}
